import { computed, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { makeShortcutManager, type ShortcutConfigurations } from 'client/library/shortcut-manager'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'

export const useEmbedEditorShortcuts = () => {
  const { editor: editorState, workbook } = storeToRefs(useWorkspaceStore())
  const workbookEditor = computed(() => workbook.value?.workbookEditor)
  const shortcutManager = makeShortcutManager()

  const preventAndExecute = (event: KeyboardEvent, actionName: string, options?: { skipEditing?: boolean; preventDefault?: boolean }) => {
    if (options?.skipEditing && workbookEditor.value?.isEditingTitle.value) return
    const action = workbook.value?.getAction(actionName)
    if (!action?.isEnabled) return
    if (options?.preventDefault !== false) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
    action.execute()
  }

  const buildShortcutConfigurations = (): ShortcutConfigurations => {
    const wb = workbook.value
    if (!wb) return []

    return [
      {
        composition: 'Escape',
        actionWithEvent: (e: KeyboardEvent) => {
          if (!workbookEditor.value?.isEditingTitle.value) return
          e.preventDefault()
          e.stopImmediatePropagation()
          wb.getAction('editor.hideEditBox')?.execute()
        },
      },
      {
        composition: 'Space',
        action: wb.getAction('editor.showEditBox'),
      },
      {
        composition: 'Shift + Left',
        preventDefault: false,
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.selectionNavigateLeftWithShift'),
      },
      {
        composition: 'Alt + Left',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.exchangeSiblingTopicLeft'),
      },
      {
        composition: 'Shift + Right',
        preventDefault: false,
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.selectionNavigateRightWithShift'),
      },
      {
        composition: 'Alt + Right',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.exchangeSiblingTopicRight'),
      },
      {
        composition: 'Shift + Up',
        preventDefault: false,
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.selectionNavigateUpWithShift'),
      },
      {
        composition: 'Alt + Up',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.exchangeSiblingTopicUp'),
      },
      {
        composition: 'Shift + Down',
        preventDefault: false,
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.selectionNavigateDownWithShift'),
      },
      {
        composition: 'Alt + Down',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.exchangeSiblingTopicDown'),
      },
      {
        composition: 'Enter',
        action: wb.getAction('editor.addTopic'),
      },
      {
        composition: 'Shift + Enter',
        name: 'addTopicBefore',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.addTopicBefore', { skipEditing: true }),
      },
      {
        composition: 'CmdOrCtrl + Enter',
        name: 'addParentTopic',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'editor.addParentTopic', { skipEditing: true }),
      },
      {
        compositions: ['TAB', 'Insert'],
        name: 'addSubTopic',
        actionWithEvent: (e: KeyboardEvent) => {
          if (workbookEditor.value?.isEditingTitle.value) return
          const action = wb.getAction('editor.addSubTopic')
          if (!action?.isEnabled) return
          e.preventDefault()
          e.stopImmediatePropagation()
          action.execute()
        },
      },
      {
        compositions: ['Backspace', 'Delete'],
        action: wb.getAction('delete'),
      },
      {
        composition: 'CmdOrCtrl + Backspace',
        action: wb.getAction('deleteSingleTopic'),
      },
      {
        composition: 'CmdOrCtrl + Shift + R',
        action: wb.getAction('editor.addRelationship'),
      },
      {
        composition: 'CmdOrCtrl + Shift + B',
        action: wb.getAction('editor.addBoundary'),
      },
      {
        composition: 'CmdOrCtrl + A',
        action: wb.getAction('selectAll'),
      },
      {
        composition: 'CmdOrCtrl + Z',
        action: wb.getAction('undo'),
      },
      {
        composition: 'CmdOrCtrl + Shift + Z',
        name: 'redo',
        actionWithEvent: (e: KeyboardEvent) => preventAndExecute(e, 'redo'),
      },
      {
        composition: 'CmdOrCtrl + C',
        action: wb.getAction('copy'),
      },
      {
        composition: 'CmdOrCtrl + X',
        action: wb.getAction('cut'),
      },
      {
        composition: 'CmdOrCtrl + V',
        action: wb.getAction('paste'),
        preventDefault: false,
      },
      {
        composition: 'CmdOrCtrl + =',
        action: wb.getAction('editor.zoomIn'),
      },
      {
        composition: 'CmdOrCtrl + -',
        action: wb.getAction('editor.zoomOut'),
      },
      {
        composition: 'CmdOrCtrl + 0',
        action: wb.getAction('editor.fitMap'),
      },
      {
        composition: 'CmdOrCtrl + /',
        action: wb.getAction('editor.toggleBranch'),
      },
      {
        composition: 'CmdOrCtrl + Alt + /',
        args: { spread: true },
        action: wb.getAction('editor.toggleAllBranch'),
      },
      {
        composition: 'CmdOrCtrl + R',
        action: wb.getAction('editor.focusAndSelectCenter'),
      },
    ]
  }

  const refreshShortcuts = () => {
    if (typeof window === 'undefined') return
    if (editorState.value?.mapStatus !== 'loaded' || !workbook.value) {
      shortcutManager.stopListen()
      return
    }
    const shortcuts = buildShortcutConfigurations()
    shortcutManager.setShortcuts(shortcuts)
    shortcutManager.stopListen()
    shortcutManager.startListen({ capture: true, target: window })
  }

  const stopWatch = watch([() => editorState.value?.mapStatus, () => workbook.value], () => refreshShortcuts(), { immediate: true })

  onUnmounted(() => {
    stopWatch()
    shortcutManager.stopListen()
  })
}
